import { z } from 'zod'
import {
  ShowPackageMappingDecision,
  ShowPackageReceipt,
  ShowPackageReference,
} from './show-package.js'

const Key = z.string().min(1).max(256)
export const ShowPackageImportResolution = z.strictObject({
  reference: ShowPackageReference,
  field: Key,
  action: z.enum(['usePackage', 'keepDestination']),
})
// Typed club names are unbounded package text, so their keys are too.
const AffiliationKey = z.string().min(1)
/**
 * Typed club names without a club reference are linked to a club with the same normalized name.
 * A choice selects a destination club or, by its stable key, a club record in the file; a null
 * destination keeps the typed name unlinked, and `create` adds a club when none matches. Keep and
 * link decisions stand on later corrections until replaced.
 */
export const ShowPackageClubAffiliationChoice = z.union([
  z.strictObject({ nameKey: AffiliationKey, destinationId: Key.nullable() }),
  z.strictObject({ nameKey: AffiliationKey, clubKey: Key }),
  z.strictObject({ nameKey: AffiliationKey, create: z.literal(true) }),
])
export const ShowPackageImportRequest = z.strictObject({
  package: z.unknown(),
  baselines: z.array(z.unknown()).min(1).max(64).optional(),
  mappings: z.array(ShowPackageMappingDecision).default([]),
  /** Retired with the previous-edition link. Accepted from older clients and ignored. */
  priorClasses: z
    .array(
      z.strictObject({
        priorEditionKey: Key,
        classKey: Key,
        destinationId: Key,
      })
    )
    .default([]),
  resolutions: z.array(ShowPackageImportResolution).default([]),
  clubAffiliations: z.array(ShowPackageClubAffiliationChoice).default([]),
})
export const ShowPackageImportApplyRequest = ShowPackageImportRequest.extend({
  reviewToken: z.uuid(),
})
export const ShowPackageImportChange = z.strictObject({
  reference: ShowPackageReference,
  action: z.enum(['create', 'update', 'remove', 'preserve', 'map', 'conflict']),
  field: z.string().optional(),
  allowedResolutions: z.array(z.enum(['keepDestination', 'usePackage'])).optional(),
  before: z.unknown().optional(),
  incoming: z.unknown().optional(),
  destinationId: z.string().optional(),
})
export const ShowPackageImportCreationContext = z.strictObject({
  sharedPools: z.array(
    z.strictObject({
      type: z.enum([
        'organizations',
        'people',
        'exhibitors',
        'clubs',
        'models',
        'subtypes',
        'series',
        'seriesIdentities',
      ]),
      owner: ShowPackageReference.nullable(),
      destinationOwnerId: z.string().nullable(),
      occupied: z.boolean().nullable(),
    })
  ),
  hasPortableBindings: z.boolean(),
  show: z.strictObject({ exists: z.boolean(), hasLineage: z.boolean() }),
  proposedCreates: z.array(ShowPackageReference),
})
export const ShowPackageImportDestinationRecord = z.strictObject({
  type: ShowPackageReference.shape.type,
  destinationId: z.string(),
  label: z.string(),
  secondary: z.string(),
})
export const ShowPackageImportClubAffiliation = z.strictObject({
  nameKey: z.string(),
  name: z.string(),
  affiliations: z.number().int().nonnegative(),
  action: z.enum(['link', 'create', 'keepName', 'review']),
  destinationId: z.string().nullable(),
  candidates: z.array(
    z.strictObject({
      destinationId: z.string(),
      clubKey: z.string().nullable(),
      label: z.string(),
      secondary: z.string(),
    })
  ),
})
export const ShowPackageImportPreview = z.strictObject({
  destinationShowId: z.string(),
  creationContext: ShowPackageImportCreationContext,
  destinationRecords: z.array(ShowPackageImportDestinationRecord),
  clubAffiliations: z.array(ShowPackageImportClubAffiliation).default([]),
  /** Retired previous-edition links and pairwise class matches found in the file; never imported. */
  ignoredLegacy: z
    .strictObject({
      priorEditions: z.number().int().nonnegative(),
      editionMappings: z.number().int().nonnegative(),
    })
    .default({ priorEditions: 0, editionMappings: 0 }),
  reviewToken: z.uuid(),
  expiresAt: z.iso.datetime(),
  canApply: z.boolean(),
  contentDigest: z.string(),
  changes: z.array(ShowPackageImportChange),
  blockers: z.array(
    z.strictObject({
      code: z.string(),
      reference: ShowPackageReference.optional(),
      message: z.string(),
    })
  ),
})
export const ShowPackageImportResult = z.strictObject({
  destinationShowId: z.string(),
  receipt: ShowPackageReceipt,
  alreadyApplied: z.boolean(),
})
export type TShowPackageImportRequest = z.infer<typeof ShowPackageImportRequest>
export type TShowPackageImportPreview = z.infer<typeof ShowPackageImportPreview>
export type TShowPackageImportChange = z.infer<typeof ShowPackageImportChange>
export type TShowPackageImportClubAffiliation = z.infer<typeof ShowPackageImportClubAffiliation>

/** Discovery only: preview/apply remain authoritative about mapping eligibility. */
export const ShowPackageCandidateRequest = z.strictObject({
  type: z.enum([
    'show',
    'organizations',
    'people',
    'exhibitors',
    'clubs',
    'models',
    'subtypes',
    'series',
    'seriesIdentities',
    'divisions',
    'classes',
    'staff',
    'ruleSnapshots',
  ]),
  showId: Key.optional(),
  search: z.string().max(120).default(''),
})
export const ShowPackageCandidates = z.strictObject({
  candidates: z.array(z.strictObject({ id: Key, label: z.string(), secondary: z.string() })),
  hasMore: z.boolean(),
})
